import React from "react";
import PropTypes from "prop-types";
import SecondaryButton from "./SecondaryButton";
import trashIcon from "../../assets/images/support/trash-icon.svg";
import cancelIcon from "../../assets/images/user-dashboard/remove-circle.svg";
import tickIcon from "../../assets/images/support/tick-circle-white.svg";

/* -------------------------------------------------------------------------
 * DeleteConfirmationModal — reusable modal for delete confirmation
 *
 * Props:
 * - isOpen: boolean to control modal visibility
 * - title: string for modal title
 * - message: string for modal message
 * - onCancel: function to handle cancel action
 * - onConfirm: function to handle confirm action
 * - itemName: optional string to customize the message (defaults to "item")
 * ---------------------------------------------------------------------- */

const DeleteConfirmationModal = ({
  isOpen,
  title,
  message,
  onCancel,
  onConfirm,
  itemName = "item",
  confirmButtonText = "Delete",
  confirmButtonIcon = null,
  confirmButtonVariant = null,
  confirmButtonDisabled = false,
  confirmButtonLoading = false,
}) => {
  if (!isOpen) return null;

  // Auto-determine appropriate icon and variant based on button text if not provided
  const getButtonConfig = () => {
    if (confirmButtonIcon && confirmButtonVariant) {
      return { icon: confirmButtonIcon, variant: confirmButtonVariant };
    }

    const buttonText = confirmButtonText.toLowerCase();
    
    if (buttonText.includes("delete") || buttonText.includes("remove") || buttonText.includes("unlink")) {
      return { icon: trashIcon, variant: "light-red" };
    }
    if (buttonText.includes("restore") || buttonText.includes("reactivate") || buttonText.includes("unarchive")) {
      return { icon: tickIcon, variant: "light-orange" };
    }
    if (buttonText.includes("archive")) {
      return { icon: tickIcon, variant: "light-orange" };
    }
    if (buttonText.includes("run") || buttonText.includes("ok") || buttonText.includes("proceed")) {
      return { icon: tickIcon, variant: "light-orange" };
    }
    
    // Default fallback
    return { icon: trashIcon, variant: "light-red" };
  };

  const { icon: finalIcon, variant: finalVariant } = getButtonConfig();

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 999999,
      }}
      onClick={onCancel}
    >
      <div
        style={{
          backgroundColor: "white",
          padding: "24px",
          borderRadius: "12px",
          maxWidth: "400px",
          width: "90%",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3
          style={{
            marginBottom: "12px",
            fontSize: "20px",
            fontWeight: 600,
            color: "#1a1a1a",
          }}
        >
          {title}
        </h3>
        <p
          style={{
            marginBottom: "24px",
            fontSize: "14px",
            color: "#666",
            lineHeight: "1.5",
          }}
        >
          {message || `Are you sure you want to delete this ${itemName}? This action cannot be undone.`}
        </p>
        <div
          style={{
            display: "flex",
            gap: "12px",
            justifyContent: "flex-end",
          }}
        >
          <SecondaryButton
            type="button"
            variant="light-grey"
            onClick={onCancel}
            icon={cancelIcon}
            disabled={confirmButtonDisabled}
          >
            Cancel
          </SecondaryButton>
          <SecondaryButton
            type="button"
            variant={finalVariant}
            onClick={onConfirm}
            icon={finalIcon}
            disabled={confirmButtonDisabled}
            loading={confirmButtonLoading}
          >
            {confirmButtonText}
          </SecondaryButton>
        </div>
      </div>
    </div>
  );
};

DeleteConfirmationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  title: PropTypes.string,
  message: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  itemName: PropTypes.string,
  confirmButtonText: PropTypes.string,
  confirmButtonIcon: PropTypes.string,
  confirmButtonVariant: PropTypes.string,
  confirmButtonDisabled: PropTypes.bool,
  confirmButtonLoading: PropTypes.bool,
};

DeleteConfirmationModal.defaultProps = {
  title: "Delete",
  message: "",
  itemName: "item",
};

export default DeleteConfirmationModal;
